import React, { useEffect, useMemo, useState } from "react";
import { Plus, SquarePen, Trash2, Users, CheckCircle2, XCircle } from "lucide-react";
import { getAvatarSolid, getAvatarGradient } from "../../../utils/avatarUtils";
import { getInitials } from "../../common/Table2/cells";
import toast from "react-hot-toast";

import { ApprovalGroup, GroupDialog } from "./GroupDialog";
import { TableSkeleton } from "../../../utils/SkeletonUtils";
import PageHeader from "../../common/PageHeader";
import StatsCards, { StatCardConfig } from "../../common/StatsCards";
import Button from "../../common/Button";
import Badge from "../../common/Badge";
import { SimpleTooltip, ConfirmationModal, SearchInput } from "../../common";
import {
  DataTable,
  DataTableCard,
  Pagination2,
} from "../../common/Table2";
import { ColumnSelector } from "../../common/Table/ColumnSelector";
import type { TableColumn } from "../../common/Table";
import {
  useGetApprovalGroupStatsQuery,
  ApprovalGroupType,
  IApprovalGroup,
  useCreateApprovalGroupMutation,
  useDeleteApprovalGroupMutation,
  useGetApprovalGroupsQuery,
  useUpdateApprovalGroupMutation,
} from "../../../store/apis/dynamicWorkflow.api";
import { useGetUsersForFilterQuery } from "../../../store/apis/user.api";
import { useGetMasterConfigByCategoryQuery } from "../../../store/apis/masterConfig.api";
import { getLabelValue, getStatusLabel } from "../../../utils/dynamicworkflow/workflowUtils";
import { useDebounce } from "../../../utils/debounce";

const OPTIONAL_KEYS = ["description", "status"];

const statCards: StatCardConfig[] = [
  {
    key: "total",
    label: "Total Groups",
    icon: Users,
    iconBg: "var(--color-info-light)",
    iconColor: "var(--color-info)",
  },
  {
    key: "active",
    label: "Active Groups",
    icon: CheckCircle2,
    iconBg: "var(--color-success-light)",
    iconColor: "var(--color-success-dark)",
  },
  {
    key: "inactive",
    label: "Inactive Groups",
    icon: XCircle,
    iconBg: "var(--color-error-light)",
    iconColor: "var(--color-error-dark)",
  },
];

const ManageGroups = () => {
  const { data: employees } = useGetUsersForFilterQuery();
  const { data: statusData, isLoading: isLoadingStatus } =
    useGetMasterConfigByCategoryQuery("group_status", {
      refetchOnMountOrArgChange: true,
    });

  const statusOption = useMemo(() => {
    if (!statusData) return [];
    return statusData.map((s) => ({
      value: s.id,
      label: s.displayName ?? s.filterCode,
    }));
  }, [statusData]);

  const [createApprovalGroup, { isLoading: isCreating }] =
    useCreateApprovalGroupMutation();
  const [updateApprovalGroup, { isLoading: isUpdating }] =
    useUpdateApprovalGroupMutation();

  const [groups, setGroups] = useState<ApprovalGroup[]>([]);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [visibleKeys, setVisibleKeys] = useState<string[]>(OPTIONAL_KEYS);

  const debouncedSearch = useDebounce(search, 500);
  const { data: groupsData, isLoading } = useGetApprovalGroupsQuery({
    page,
    limit,
    search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
  });
  const [deleteGroup, { isLoading: isDeleting }] =
    useDeleteApprovalGroupMutation();

  const { data: groupStats } = useGetApprovalGroupStatsQuery();

  useEffect(() => {
    if (groupsData?.data?.items) {
      setGroups(
        groupsData.data.items.map((g) => ({
          ...g,
          description: g.description || "",
          status: (g.status as "active" | "inactive") || "active",
          memberIds: g.memberIds || [],
          groupType: g.groupType,
          isSystemGroup: g.isSystemGroup,
        }))
      );
    }
  }, [groupsData]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ApprovalGroup | null>(null);

  const handleDelete = async (id: string) => {
    try {
      await deleteGroup(id).unwrap();
      toast.success("The group has been removed.");
    } catch {
      toast.error("Failed to delete group.");
    }
  };

  const handleEdit = (id: string) => {
    const groupToEdit = groups.find((g) => g.id === id);
    if (groupToEdit) {
      setEditingGroup(groupToEdit);
      setDialogOpen(true);
    }
  };

  const handleCreateClick = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };

  const handleSaveGroup = async (data: IApprovalGroup) => {
    try {
      if (editingGroup) {
        await updateApprovalGroup({ id: editingGroup.id!, data }).unwrap();
      } else {
        await createApprovalGroup(data).unwrap();
      }
      setDialogOpen(false);
      toast.success("Group saved successfully.");
    } catch {
      toast.error("Failed to save group.");
    }
  };

  const toggleVisibility = (key: string) => {
    setVisibleKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const columns = useMemo<TableColumn<ApprovalGroup>[]>(() => [
    {
      key: "name",
      title: "Group",
      label: "Group",
      required: true,
      truncate: false,
      render: (_: unknown, record: ApprovalGroup, index?: number) => (
        <div className="flex items-center gap-[11px]">
          <div
            className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center text-white font-bold text-[13.5px] flex-none"
            style={{ background: getAvatarGradient(record.name || "") }}
          >
            {getInitials(record.name)}
          </div>
          <div className="min-w-0 flex items-center gap-2">
            <span className="text-[13.5px] font-bold text-[var(--color-text-primary)] truncate">
              {record.name}
            </span>
            {record.isSystemGroup && (
              <Badge variant="blue">System</Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "description",
      title: "Description",
      label: "Description",
      dataIndex: "description",
      render: (_value: unknown, record: ApprovalGroup) => {
        const text = record.description || "";
        return (
          <SimpleTooltip label={text || "No Description"} side="top" delay={500}>
            <div className="max-w-[300px] truncate text-[13px] text-[var(--color-text-secondary)]">
              {text || "—"}
            </div>
          </SimpleTooltip>
        );
      },
    },
    {
      key: "members",
      title: "Members",
      label: "Members",
      dataIndex: "memberIds",
      render: (_value: unknown, record: ApprovalGroup) => {
        const memberIds = (record.memberIds || []) as string[];
        if (record.isSystemGroup) {
          const label =
            record.groupType === ApprovalGroupType.REPORTING_AUTHORITY
              ? "Requester's Reporting Manager"
              : "Requester's Functional Manager";
          return (
            <span className="bg-[var(--color-info-light)] text-[var(--color-info)] text-xs rounded-full py-1 px-3 inline-block">
              {label}
            </span>
          );
        }
        if (!memberIds || memberIds.length === 0) {
          return (
            <span className="text-[var(--color-text-tertiary)] text-xs">
              No Members
            </span>
          );
        }
        const memberNames = memberIds
          .map((id) => employees?.find((e) => e.id === id)?.fullName)
          .filter(Boolean) as string[];
        return (
          <div className="flex items-center">
            <div className="flex items-center">
              {memberNames.slice(0, 3).map((name, i) => (
                <div
                  key={i}
                  className="w-7 h-7 rounded-lg grid place-items-center text-white font-bold text-[10.5px] border-2 border-white"
                  style={{
                    background: getAvatarGradient(name),
                    marginLeft: i === 0 ? 0 : -8,
                    zIndex: 3 - i,
                  }}
                >
                  {getInitials(name)}
                </div>
              ))}
            </div>
            <span className="ml-1.5 text-xs font-bold text-[var(--color-text-secondary)]">
              {memberIds.length} members
            </span>
          </div>
        );
      },
    },
    {
      key: "status",
      title: "Status",
      label: "Status",
      dataIndex: "status",
      render: (_value: unknown, record: ApprovalGroup) => {
        const status = record.status as string | undefined;
        return (
          <Badge
            variant={
              getStatusLabel(statusData, status)?.toLowerCase() === "active"
                ? "green"
                : "red"
            }
          >
            {getStatusLabel(statusData, status)}
          </Badge>
        );
      },
    },
    {
      key: "actions",
      title: "Actions",
      label: "Actions",
      required: true,
      truncate: false,
      align: "center",
      render: (_: unknown, record: ApprovalGroup) => {
        if (record.isSystemGroup) {
          return (
            <span className="text-xs text-[var(--color-text-tertiary)] italic">
              Non-editable
            </span>
          );
        }
        return (
          <div className="flex items-center justify-center gap-1">
            <SimpleTooltip label="Edit" side="top">
              <div>
                <button
                  type="button"
                  className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-muted)]"
                  onClick={() => handleEdit(record?.id || "")}
                >
                  <SquarePen className="w-4 h-4" />
                </button>
              </div>
            </SimpleTooltip>
            <SimpleTooltip label="Delete" side="top">
              <div>
                <button
                  type="button"
                  className="w-[34px] h-[34px] grid place-items-center border-none bg-transparent rounded-[9px] cursor-pointer text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-error-light)] hover:text-[var(--color-error-dark)]"
                  onClick={() => {
                    setConfirmDialog(true);
                    setSelectedGroupId(record?.id || "");
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </SimpleTooltip>
          </div>
        );
      },
    },
  ], [statusData, employees, groups]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.required || visibleKeys.includes(c.key)),
    [columns, visibleKeys]
  );

  return (
    <>
      <div className="max-w-full mx-auto">
        <div className="space-y-6">
          <PageHeader
            title="Manage Groups"
            description="Reusable people groups used across approvals, surveys and workflows"
            breadcrumbs={[{ label: "Home" }, { label: "Manage Groups" }]}
            actions={
              <Button
                appearance="primary"
                onClick={handleCreateClick}
                icon={<Plus className="w-4 h-4" />}
              >
                New Group
              </Button>
            }
          />

          <StatsCards
            cards={statCards}
            stats={groupStats?.data || null}
          />

          <DataTableCard
            title="Groups"
            count={groupsData?.data?.pagination?.total || 0}
            toolbar={
              <>
                <SearchInput
                  placeholder="Search groups..."
                  value={search}
                  onChange={setSearch}
                  className="w-full sm:w-[220px]"
                />
                <ColumnSelector
                  columns={columns}
                  visibleKeys={visibleKeys}
                  onToggle={toggleVisibility}
                />
              </>
            }
            pagination={
              <Pagination2
                total={groupsData?.data?.pagination?.total || 0}
                page={page}
                pageSize={limit}
                pageSizeOptions={[5, 10, 20, 50]}
                onPageChange={setPage}
                onPageSizeChange={(newLimit) => {
                  setLimit(newLimit);
                  setPage(1);
                }}
              />
            }
          >
            <DataTable
              columns={visibleColumns}
              rows={groups}
              rowKey={(r) => r.id || ""}
              loading={isLoading}
              skeleton={<TableSkeleton rows={limit} columns={visibleColumns.length} />}
              emptyMessage="No groups found"
              responsive="scroll"
            />
          </DataTableCard>
        </div>
      </div>

      <GroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialData={editingGroup}
        statusOption={statusOption}
        onSave={handleSaveGroup}
        isLoading={isCreating || isUpdating}
      />

      <ConfirmationModal
        type="danger"
        isLoading={isDeleting}
        isOpen={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        title="Delete Group"
        message="Are you sure you want to delete this group?"
        onConfirm={() => {
          handleDelete(selectedGroupId || "");
          setConfirmDialog(false);
        }}
      />
    </>
  );
};

export default ManageGroups;
